import { validateAttributeValueWithSpec } from './attribute-specs.js';
import { decodeGs1Segment, isRestrictedCirculationGtin, isValidCharset, isValidGs1DateAi, verifyCheckDigit, } from './check-digit.js';
export function validateAttributeValue(ai, rawVal) {
    return validateAttributeValueWithSpec(ai, rawVal, {
        decodeGs1Segment,
        isValidGs1DateAi,
        isValidCharset,
        verifyCheckDigit,
    });
}
function checkRangeAndCharset(val, min, max, charset, lenMessage, lenClause, charMessage, charClause) {
    if (val.length < min || val.length > max) {
        return {
            ok: false,
            errorCode: 'GS1_SYNTAX_INVALID_KEY_LENGTH',
            message: lenMessage,
            clauseRef: lenClause,
        };
    }
    if (!isValidCharset(val, charset)) {
        return {
            ok: false,
            errorCode: 'GS1_SYNTAX_INVALID_CHARACTER_SET',
            message: charMessage,
            clauseRef: charClause,
        };
    }
    return null;
}
function checkFixedNumericWithMod10(val, expectedLength, keyLabel, lenClause = 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.3, pp22–23; §4.5, pp23–24') {
    if (!isValidCharset(val, 'NUMERIC') || val.length !== expectedLength) {
        return {
            ok: false,
            errorCode: 'GS1_SYNTAX_INVALID_KEY_LENGTH',
            message: `${keyLabel} must be exactly ${String(expectedLength)} numeric digits.`,
            clauseRef: lenClause,
        };
    }
    if (!verifyCheckDigit(val)) {
        return {
            ok: false,
            errorCode: 'GS1_SYNTAX_INVALID_CHECK_DIGIT',
            message: `${keyLabel} failed mod-10 check digit verification.`,
            clauseRef: 'GS1 General Specifications Release 26.0 §7.9',
        };
    }
    return { ok: true, canonicalKey: val };
}
export function validatePrimaryKeyValue(ai, rawVal) {
    const decodedRes = decodeGs1Segment(rawVal);
    if (!decodedRes.ok) {
        return {
            ok: false,
            errorCode: 'GS1_SYNTAX_INVALID_CHARACTER_SET',
            message: 'Malformed percent-encoding in primary key value.',
            clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.2, pp21–22',
        };
    }
    const val = decodedRes.value;
    switch (ai) {
        case '01': {
            if (!isValidCharset(val, 'NUMERIC')) {
                return {
                    ok: false,
                    errorCode: 'GS1_SYNTAX_INVALID_CHARACTER_SET',
                    message: 'GTIN (01) must contain numeric digits only.',
                    clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.3, pp22–23; §4.5, p23',
                };
            }
            if (val.length !== 8 && val.length !== 12 && val.length !== 13 && val.length !== 14) {
                return {
                    ok: false,
                    errorCode: 'GS1_SYNTAX_INVALID_KEY_LENGTH',
                    message: 'GTIN (01) must be 8, 12, 13, or 14 digits.',
                    clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.1, p20; §4.3, pp22–23',
                };
            }
            const padded = val.padStart(14, '0');
            if (!verifyCheckDigit(padded)) {
                return {
                    ok: false,
                    errorCode: 'GS1_SYNTAX_INVALID_CHECK_DIGIT',
                    message: `GTIN (${val}) failed GS1 mod-10 check digit verification.`,
                    clauseRef: 'GS1 General Specifications Release 26.0 §7.9',
                };
            }
            if (isRestrictedCirculationGtin(val, padded)) {
                return {
                    ok: false,
                    errorCode: 'GS1_SYNTAX_RCN_NOT_PERMITTED',
                    message: `GTIN (${val}) uses a Restricted Circulation Number (RCN) prefix and cannot be resolved publicly.`,
                    clauseRef: 'GS1 General Specifications Release 26.0 §1.2.2.2.1, p24; Table 1-4, p26; Table 1-5, p27',
                };
            }
            return { ok: true, canonicalKey: padded };
        }
        case '8006': {
            if (!isValidCharset(val, 'NUMERIC') || val.length !== 18) {
                return {
                    ok: false,
                    errorCode: 'GS1_SYNTAX_INVALID_KEY_LENGTH',
                    message: 'ITIP (8006) must be exactly 18 numeric digits.',
                    clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.3, pp22–23; §4.5, p23',
                };
            }
            const gtinPart = val.slice(0, 14);
            const piece = Number(val.slice(14, 16));
            const total = Number(val.slice(16, 18));
            if (!verifyCheckDigit(gtinPart)) {
                return {
                    ok: false,
                    errorCode: 'GS1_SYNTAX_INVALID_CHECK_DIGIT',
                    message: 'ITIP (8006) GTIN portion failed mod-10 check digit verification.',
                    clauseRef: 'GS1 General Specifications Release 26.0 §7.9',
                };
            }
            if (piece < 1 || total < 2 || piece > total) {
                return {
                    ok: false,
                    errorCode: 'GS1_SYNTAX_INVALID_KEY_LENGTH',
                    message: 'ITIP (8006) piece count must satisfy 01 <= piece <= total and total >= 02.',
                    clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.5, p23',
                };
            }
            if (isRestrictedCirculationGtin(gtinPart, gtinPart)) {
                return {
                    ok: false,
                    errorCode: 'GS1_SYNTAX_RCN_NOT_PERMITTED',
                    message: 'ITIP (8006) contains a Restricted Circulation GTIN prefix.',
                    clauseRef: 'GS1 General Specifications Release 26.0 §1.2.2.2.1, p24; Table 1-4, p26',
                };
            }
            return { ok: true, canonicalKey: val };
        }
        case '8013': {
            const err = checkRangeAndCharset(val, 1, 25, 'XCHAR', 'GMN (8013) must be between 1 and 25 characters.', 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.3, pp22–23; §4.5, p23', 'GMN (8013) contains characters outside GS1 Table 7-20.', 'GS1 General Specifications Release 26.0 Table 7-20, p551');
            return err ?? { ok: true, canonicalKey: val };
        }
        case '8010': {
            const err = checkRangeAndCharset(val, 1, 30, 'YCHAR', 'CPID (8010) must be between 1 and 30 characters.', 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.3, pp22–23; §4.5, p23', 'CPID (8010) contains characters outside GS1 Table 7-21 (YCHAR).', 'GS1 General Specifications Release 26.0 Table 7-21, p552');
            return err ?? { ok: true, canonicalKey: val };
        }
        case '414':
        case '415':
        case '417':
            return checkFixedNumericWithMod10(val, 13, `GLN (${ai})`);
        case '8017':
        case '8018':
        case '00':
            return checkFixedNumericWithMod10(val, 18, `Key (${ai})`);
        case '402':
            return checkFixedNumericWithMod10(val, 17, 'GSIN (402)');
        case '255': {
            if (!isValidCharset(val, 'NUMERIC') || val.length < 13 || val.length > 25) {
                return {
                    ok: false,
                    errorCode: 'GS1_SYNTAX_INVALID_KEY_LENGTH',
                    message: 'GCN (255) must be 13 to 25 numeric digits.',
                    clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.3, pp22–23; §4.5, p23',
                };
            }
            if (!verifyCheckDigit(val.slice(0, 13))) {
                return {
                    ok: false,
                    errorCode: 'GS1_SYNTAX_INVALID_CHECK_DIGIT',
                    message: 'GCN (255) failed mod-10 check digit verification at position 13.',
                    clauseRef: 'GS1 General Specifications Release 26.0 §7.9',
                };
            }
            return { ok: true, canonicalKey: val };
        }
        case '253': {
            if (val.length < 13 || val.length > 30) {
                return {
                    ok: false,
                    errorCode: 'GS1_SYNTAX_INVALID_KEY_LENGTH',
                    message: 'GDTI (253) must be 13 to 30 characters.',
                    clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.3, pp22–23; §4.5, p24',
                };
            }
            const prefix13 = val.slice(0, 13);
            if (!isValidCharset(prefix13, 'NUMERIC') || !isValidCharset(val, 'XCHAR')) {
                return {
                    ok: false,
                    errorCode: 'GS1_SYNTAX_INVALID_CHARACTER_SET',
                    message: 'GDTI (253) requires 13 numeric digits followed by optional XCHAR serial.',
                    clauseRef: 'GS1 General Specifications Release 26.0 Table 7-20, p551',
                };
            }
            if (!verifyCheckDigit(prefix13)) {
                return {
                    ok: false,
                    errorCode: 'GS1_SYNTAX_INVALID_CHECK_DIGIT',
                    message: 'GDTI (253) failed mod-10 check digit verification at position 13.',
                    clauseRef: 'GS1 General Specifications Release 26.0 §7.9',
                };
            }
            return { ok: true, canonicalKey: val };
        }
        case '401':
        case '8004': {
            const err = checkRangeAndCharset(val, 1, 30, 'XCHAR', `Key (${ai}) must be 1 to 30 characters.`, 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.3, pp22–23; §4.5, p24', `Key (${ai}) contains characters outside GS1 Table 7-20.`, 'GS1 General Specifications Release 26.0 Table 7-20, p551');
            return err ?? { ok: true, canonicalKey: val };
        }
        case '8003': {
            if (val.length < 14 || val.length > 30 || !val.startsWith('0')) {
                return {
                    ok: false,
                    errorCode: 'GS1_SYNTAX_INVALID_KEY_LENGTH',
                    message: 'GRAI (8003) must start with 0 followed by 13 digits and optional 1–16 XCHAR serial.',
                    clauseRef: 'GS1 Digital Link Standard: URI Syntax v1.7.0 §4.3, pp22–23; §4.5, p24',
                };
            }
            const gtin13Slice = val.slice(1, 14);
            if (!isValidCharset(gtin13Slice, 'NUMERIC') || !isValidCharset(val, 'XCHAR')) {
                return {
                    ok: false,
                    errorCode: 'GS1_SYNTAX_INVALID_CHARACTER_SET',
                    message: 'GRAI (8003) contains invalid characters.',
                    clauseRef: 'GS1 General Specifications Release 26.0 Table 7-20, p551',
                };
            }
            if (!verifyCheckDigit(gtin13Slice)) {
                return {
                    ok: false,
                    errorCode: 'GS1_SYNTAX_INVALID_CHECK_DIGIT',
                    message: 'GRAI (8003) failed mod-10 check digit verification at position 14.',
                    clauseRef: 'GS1 General Specifications Release 26.0 §7.9',
                };
            }
            return { ok: true, canonicalKey: val };
        }
    }
}
//# sourceMappingURL=validators.js.map